import { call, takeEvery, put, select } from 'redux-saga/effects'
import { buyFormActions, BuyFormActionTypes } from '@buy/state/buy-form/actions'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { buyUserActions } from '@buy/state/user/actions'
import { buyCheckoutActions } from '@buy/state/checkout/actions'
import {
    CreateAccountRequest,
    BuyUserState,
    CreateAccountResponse,
    CreateCartRequest,
    CreateCartResponse,
    SetupTransactionResponse,
    SetupTransactionRequest,
    SetupTransactionRequestProduct,
    CreateV3CartRequestCartItem,
    ApplyPromoCodeRequest,
} from '@buy/state/user/state'
import { BuyCalculatePriceAddressDataType, BuyCheckoutState, BuyTaxDetailsDataType } from '@buy/state/checkout/state'
import { createUserUcsSaga } from '@buy/saga/user/create-user-ucs'
import { getBillingAddressValues } from '@buy/utils/get-billing-address-values'
import { getCartItems } from '@buy/utils/get-item-values'
import {
    getBuyFormState,
    getCheckoutState,
    getContentData,
    getCouponState,
    getUserState,
    getPlanData,
    getProductSettingsData,
} from '@buy/state/selectors'
import { getFlexState, getLocationState } from '@common/state/selectors'
import { CHECKOUTFORM_STEPS, BUY_TRACKING_COMPONENTS, BUY_USER_TYPES } from '@buy/constants'
import { MarketoUserInfo } from '@common/state/marketing/state'
import { makeMarketoObject } from '@common/utils'
import { LocationState } from '@common/state/location/state'
import { FlexState } from '@common/state/flex/state'
import { BuyContentData } from '@buy/state/content-data'
import { buyTrackingActions } from '@buy/state/tracking/actions'
import { TRACKING_EVENTS, VIEW_PATH } from '@common/constants'
import { getSubscriptionInfo } from '@buy/utils'
import { BuyPlan } from '@buy/state/plan'
import { BuyFormState } from '@buy/state/buy-form/state'
import { BuyCouponState } from '@buy/state/coupon/state'
import { isEmpty } from 'lodash-es'
import authClass, { getTokenFromLocalStorage, saveToken } from '@common/utils/auth-class'
import { FatalErrorMessageType, FATAL_ERROR_TYPE } from '@common/state/fatal-error/state'
import { Token } from '@getgo/auth-client'
import {
    applyPromoCode,
    createCartV3,
    deleteCart,
    updateTaxDetailsUCS,
    setupTransaction,
    updateBillingAddressUCS,
} from '@buy/services'
import { AxiosResponse } from 'axios'
import { sanitizeCompanyName } from '@buy/utils/sanitize-company-name'
import { marketingActions } from '@common/state/marketing/actions'
import { getContentLanguage } from '@buy/utils/language-helpers'
import { getOnePayLocaleFromContentLanguage, gotoAdminLocales } from '@buy/utils/get-onepay-locale'
import { priceWillFailToProcessErrorMessage, willPriceFailToProcess } from '@buy/utils/will-price-fail-to-process'
import { UpdateBillingAddressRequest } from '@buy/state/update-billing-address/state'
import { SetTaxDetailsRequest } from '@buy/state/set-tax-details/state'
import { ProductSettings } from '@buy/state/product-settings'
import { getTaxDetailValues } from '@buy/utils/get-tax-details'

function* submitFormSaga(action: ReturnType<typeof buyFormActions.submitFormUcs>) {
    // #region constants
    const componentNameConfirmation = BUY_TRACKING_COMPONENTS.CONFIRMATION_PAGE

    const steps: Record<CHECKOUTFORM_STEPS, boolean> = {
        [CHECKOUTFORM_STEPS.CREATE_FLEX_TOKEN]: false,
        [CHECKOUTFORM_STEPS.CARDINAL_TRIGGER_BIN_PROCESS]: false,
        [CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]: false,
        [CHECKOUTFORM_STEPS.SAVE_BILLING_INFO]: false,
        [CHECKOUTFORM_STEPS.SAVE_PAYMENT_INFO]: false,
        [CHECKOUTFORM_STEPS.CARDINAL_SEND_CHALLENGE]: false,
        [CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS]: false,
        [CHECKOUTFORM_STEPS.SUBMIT_PAYMENT]: false,
    }
    const {
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Street1,
        ZipCode,
        City,
        StateCode,
        Password: password,
        ContactPhone,
        // ExpirationDate,
        CompanyName,
    } = action.payload.values

    const checkout: BuyCheckoutState = yield select(getCheckoutState)
    const { userType, existingUserInformation }: BuyUserState = yield select(getUserState)
    const { locationInfo }: LocationState = yield select(getLocationState)
    const { keyInfo }: FlexState = yield select(getFlexState)
    const { submittoMarketo, marketoProductName, marketoSalesBrief, marketoSalesforceCampaignId }: BuyContentData =
        yield select(getContentData)
    const buyFormState: BuyFormState = yield select(getBuyFormState)
    const userAccountAlreadyCreated =
        buyFormState.steps.createUserAccount.completed ||
        userType === BUY_USER_TYPES.TRIALER ||
        userType === BUY_USER_TYPES.EXPIRED ||
        userType === BUY_USER_TYPES.CROSSPRODUCT
    steps[CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT] = userAccountAlreadyCreated
    const { productFamily }: ProductSettings = yield select(getProductSettingsData)

    const plan: BuyPlan = yield select(getPlanData)
    const selectedPlan = plan
    const coupon: BuyCouponState = yield select(getCouponState)
    const { isCouponValidAndApplied, couponServerData } = coupon
    // const couponServerData: CouponServerData | null = yield call(getActiveCouponServerData, coupon, plan)
    const isCouponValid = isCouponValidAndApplied && couponServerData && !isEmpty(couponServerData)
    const isScaEnabledForMid = keyInfo?.scaToken?.isScaEnabledForMid
    const errorMessage: FatalErrorMessageType = {
        trackErrorMessage: 'regular form submission failed',
        fatalErrorType: isScaEnabledForMid ? FATAL_ERROR_TYPE.CARDINAL_PAYMENT : undefined,
    }
    let accountKey = ''
    let transactionSessionId = ''
    // #endregion constants

    try {
        if (willPriceFailToProcess(checkout.calculatedTotal, process.env.ENVIRONMENT!, window.location.search)) {
            console.error(priceWillFailToProcessErrorMessage)
            throw new Error()
        }

        const billingAddressValues: BuyCalculatePriceAddressDataType = getBillingAddressValues(action.payload.values)
        const taxDetails: BuyTaxDetailsDataType = getTaxDetailValues(action.payload.values)
        const subscriptionInfo = getSubscriptionInfo(checkout.billingTenure.selected, selectedPlan, plan)
        const items = getCartItems(
            checkout.baseLine,
            checkout.additionalLines,
            checkout.addonLines,
            subscriptionInfo,
            checkout.billingTenure.selected,
            productFamily
        )
        const contentLanguage = getContentLanguage()

        // If they didn't specify a company name, use the sanitized email as company name
        const companyNameSanitized = sanitizeCompanyName((CompanyName || '').trim() || email)

        let userKey = ''
        // #region Create user account
        if (!userAccountAlreadyCreated) {
            // If the password is empty, then it is because this user is an existing user.
            // In that case, we can't send an empty password for an existing to UCS Create Account API, so
            // we need to populate it with something fake. The UCS create account endpoint will
            // ignore this fake password since the user already exists but it will update the address.
            // TODO: WAE1-4010: For existing users, change this to use UCS Update Billing instead of UCS Create Account
            const thePassword = password || 'FAKE_PASSWORD_FOR_EXISTING_USER'
            const locale = `${contentLanguage}_${billingAddressValues.country.toUpperCase()}`
            const userValues: CreateAccountRequest = {
                requestId: email, // Don't use fingerprint here because it's not unique to the user
                user: {
                    email,
                    password: thePassword,
                    firstName,
                    lastName,
                    phoneNumber: ContactPhone,
                    locale: gotoAdminLocales.includes(locale) ? locale : null, // null will default it to en-US in GotoAdmin BE.
                    timeZone: null,
                },
                country: billingAddressValues.country,
                productFamily, // to determine the email theme being triggered to the customer
                billingAddress: {
                    firstName,
                    lastName,
                    addressLine1: billingAddressValues.addressLine1,
                    addressLine2: billingAddressValues.addressLine1,
                    city: billingAddressValues.city,
                    state: billingAddressValues.state || null,
                    postalCode: billingAddressValues.postalCode || null,
                    country: billingAddressValues.country,
                    phoneNumber: ContactPhone,
                    ccEmails: null,
                    companyName: companyNameSanitized,
                    email,
                },
                companyName: companyNameSanitized,
                currency: locationInfo.currency,
                // ipAddress can be null because UCS gets it from the network if it's not in the payload
                ipAddress: null,
                invitationEmailExpiry: null,
                taxNumber: taxDetails.taxNumber || '',
                subTaxNumberType: taxDetails.federalTaxType || '', // "Business", "Individual" (only applies to Brazil)
                emailVerificationToken: null,
            }
            const createUserAction = buyUserActions.createNewUserUCS(userValues)
            const userData: CreateAccountResponse = yield call(createUserUcsSaga, createUserAction)

            if (userData) {
                userKey = userData.userKey
                accountKey = userData.accountKey
                steps[CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT] = true
                yield put(buyCheckoutActions.setAutoLogin(userData.autoLoginTicket))

                // If autoLoginTicket exists, perform silent authentication using hidden iframe
                // (autoLoginTicket is null in the case of an existing user performing a cross-product purchase)
                if (userData.autoLoginTicket) {
                    const silentAuthTicket = userData.autoLoginTicket
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const token: Token = yield call(
                        authClass.login,
                        window.location.pathname,
                        window.location.search,
                        false,
                        silentAuthTicket
                    )

                    if (token) {
                        // Put token in redux store and in localStorage
                        yield put(buyUserActions.setAccessToken(token))
                        yield call(saveToken, token)
                    }
                }
            }
        } else {
            // existing user
            userKey = existingUserInformation?.UserKey || ''
            accountKey = existingUserInformation?.AccountKey || ''
            const token = getTokenFromLocalStorage()
            const userToken = token?.access_token

            // #region Update billing info
            const billingData: UpdateBillingAddressRequest = {
                billingAddress: {
                    addressLine1: billingAddressValues.addressLine1,
                    city: billingAddressValues.city,
                    state: billingAddressValues.state || null,
                    country: billingAddressValues.country,
                    postalCode: billingAddressValues.postalCode || null,
                    firstName,
                    lastName,
                    email: billingAddressValues.email,
                    phoneNumber: ContactPhone,
                    companyName: companyNameSanitized,
                },
            }
            try {
                yield call(updateBillingAddressUCS as any, userToken, accountKey, billingData)
            } catch (e: any) {
                const componentName = BUY_TRACKING_COMPONENTS.CREATE_BILLING_INFO_SAGA
                yield put(
                    buyTrackingActions.track(
                        {
                            event: TRACKING_EVENTS.ERROR_PURCHASE,
                            eventData: { errorMessage: 'failed to save billing info' },
                        },
                        componentName
                    )
                )
                yield put(fatalErrorActions.setFatalError(true, { trackErrorMessage: 'failed to save billing info' }))
                return
            }
            // #endregion

            // #region Update tax info
            if (taxDetails.taxNumber) {
                try {
                    const taxDetailsData: SetTaxDetailsRequest = {
                        taxNumber: taxDetails.taxNumber || '',
                        subTaxNumberType: taxDetails.federalTaxType || '', // "Business", "Individual" (only applies to Brazil)
                        countryCode: billingAddressValues.country,
                    }
                    yield call(updateTaxDetailsUCS as any, userToken, accountKey, taxDetailsData)
                } catch (e: any) {
                    const componentName = BUY_TRACKING_COMPONENTS.CREATE_BILLING_INFO_SAGA
                    yield put(
                        buyTrackingActions.track(
                            {
                                event: TRACKING_EVENTS.ERROR_PURCHASE,
                                eventData: { errorMessage: 'failed to save tax info' },
                            },
                            componentName
                        )
                    )
                    yield put(fatalErrorActions.setFatalError(true, { trackErrorMessage: 'failed to save tax info' }))
                    return
                }
            }
            // #endregion
        }
        // #endregion

        yield put(
            buyFormActions.updateSteps(
                CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT,
                steps[CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]
            )
        )

        // #region Save billing info
        const token = getTokenFromLocalStorage()!
        const promoCode = isCouponValid ? couponServerData?.couponCode : ''

        // Delete any existing cart
        const deleteCartResponse: AxiosResponse<boolean> = yield call(deleteCart as any, token.access_token, accountKey)
        if (!(deleteCartResponse.status === 404 || deleteCartResponse.status === 200)) {
            // 404 - Commerce cart not found, so you're got to create a new cart
            throw new Error('Error while invoking DELETE v2/cart') // throw an exception if any other errors in the API
        }

        // Create Cart
        const cartItems = items.map((item) => {
            const cartItem: CreateV3CartRequestCartItem = {
                paymentPlan: item.paymentPlan,
                productFamilyKey: productFamily,
                productSku: item.productSku,
                quantity: item.quantity,
            }
            return cartItem
        })
        const cartRequestData: CreateCartRequest = {
            currency: locationInfo.currency,
            cartItems,
            promoCode,
        }

        // TODO: WAE1-4010: Remove this
        // const tempObj = {
        //     // country: locationInfo.country,
        //     // currency: locationInfo.currency,
        //     expirationMonth: ExpirationDate.substr(0, 2),
        //     expirationYear: `20${ExpirationDate.substr(3, 5)}`,
        //     products: items,
        //     grossAmount: checkout.calculatedTotal,
        //     couponCode: isCouponValid ? couponServerData?.couponCode : '',
        //     anniversaryDate: checkout.anniversaryDate,
        // }
        // console.log(tempObj)

        // Create cart
        const createCartResponse: AxiosResponse<CreateCartResponse> = yield call(
            createCartV3 as any,
            token.access_token,
            accountKey,
            cartRequestData
        )
        const createCartResponseData = createCartResponse.data

        // Apply promoCode (if any) to the cart
        if (promoCode) {
            const applyPromoCodeRequestData: ApplyPromoCodeRequest = {
                promoCode,
            }
            const applyPromoCodeResponse: AxiosResponse = yield call(
                applyPromoCode as any,
                token.access_token,
                accountKey,
                applyPromoCodeRequestData
            )
            if (applyPromoCodeResponse.status !== 200) {
                // TODO: WAE1-4010: Errror handling?
            }
        }

        // Setup Transaction
        const totalAfterTax = createCartResponseData.cartTotal + checkout.calculatedTax
        const transactionProducts = createCartResponseData.cartItems.map((cartItem) => {
            const product: SetupTransactionRequestProduct = {
                amount: totalAfterTax,
                flowName: null,
                frequency: cartItem.frequency,
                productFamily: cartItem.productFamilyKey,
                productSku: cartItem.productSku,
                publisherId: null,
                quantity: cartItem.quantity,
                subscriptionEndDate: null,
                transactionType: 'CUSTOMER_ACTION',
            }
            return product
        })

        const successRedirectUrl =
            window.location.origin +
            window.location.pathname.replace(VIEW_PATH.PATH_CHECKOUT, VIEW_PATH.PATH_CONFIRMATION)

        const setupTransactionRequestData: SetupTransactionRequest = {
            quoteId: createCartResponseData.key,
            intent: 'PaymentOrPaymentMethodChange',
            currencyCode: locationInfo.currency,
            targetOrigin: process.env.ONEPAY_SITE_URL!,
            amount: checkout.calculatedTotal,
            cancelRedirectUrl: window.location.href,
            products: transactionProducts,
            successRedirectUrl,
            taxAmount: checkout.calculatedTax,
        }

        // TODO: WAE1-4010: Move this part to its own saga with error handling
        const setupTransactionResponse: AxiosResponse<SetupTransactionResponse> = yield call(
            setupTransaction as any,
            token.access_token,
            accountKey,
            setupTransactionRequestData
        )
        const setupTransactionResponseData = setupTransactionResponse.data

        // Save userData.accountKey to sessionStorage so we can retrieve it after coming back from OnePay
        // See where it is retrieved in src/products/buy/saga/checkout-form/get-latest-invoice.ts
        window.sessionStorage.setItem('accountKey', accountKey)

        // Save setupTransactionResponseData.sessionId to sessionStorage so we can retrieve it after coming back from OnePay
        // See where it is retrieved in src/products/buy/saga/checkout-form/get-latest-invoice.ts
        transactionSessionId = setupTransactionResponseData.sessionId
        window.sessionStorage.setItem('transactionSessionId', transactionSessionId)

        if (steps[CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]) {
            steps[CHECKOUTFORM_STEPS.SAVE_BILLING_INFO] = true
            yield put(
                buyFormActions.updateSteps(
                    CHECKOUTFORM_STEPS.SAVE_BILLING_INFO,
                    steps[CHECKOUTFORM_STEPS.SAVE_BILLING_INFO]
                )
            )
        }
        // #endregion

        // #region process purchase - create and save payment method
        if (steps[CHECKOUTFORM_STEPS.SAVE_BILLING_INFO]) {
            steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT] = true
            yield put(
                buyFormActions.updateSteps(CHECKOUTFORM_STEPS.SUBMIT_PAYMENT, steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT])
            )
            yield put(
                buyTrackingActions.track(
                    { event: TRACKING_EVENTS.PURCHASE, eventData: { invoiceNumber: null } },
                    componentNameConfirmation,
                    true // true to save to sessionStorage instead of dataLayer for later use on confirmation page which will then push it to the dataLayer
                )
            )
        }
        // #endregion

        // #region Set purchase complete
        if (steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT]) {
            yield put(buyUserActions.setDisplayName(firstName))
            yield put(
                buyTrackingActions.track(
                    { event: TRACKING_EVENTS.COMPLETE_BUY_FLOW, eventData: { userEmail: email } },
                    componentNameConfirmation,
                    true // true to save to sessionStorage instead of dataLayer for later use on confirmation page which will then push it to the dataLayer
                )
            )
            if (submittoMarketo) {
                const { planKey } = plan ?? {}
                const marketoData: MarketoUserInfo = makeMarketoObject({
                    userKey,
                    accountKey,
                    firstName,
                    lastName,
                    email,
                    planKey,
                    Street1,
                    City,
                    StateCode,
                    ZipCode,
                    ContactPhone,
                    marketoSalesBrief,
                    marketoSalesforceCampaignId,
                    marketoProductName,
                    CompanyName,
                })

                // Save marketoData to sessionStorage so we can post it to AWS after coming back from OnePay
                // See where it is retrieved from session and posted to AWS in src/products/buy/views/Confirmation/index.tsx
                const saveMarketoToSessionStorage = true
                yield put(marketingActions.postToMarketo(marketoData, saveMarketoToSessionStorage))
            }

            // Redirect to OnePay
            const onePayTheme = 'light' // other option is 'dark'
            const onePayLocale = getOnePayLocaleFromContentLanguage(contentLanguage)
            const onePayUrl = `${process.env.ONEPAY_SITE_URL}/${accountKey}/${transactionSessionId}/pay?locale=${onePayLocale}&theme=${onePayTheme}`
            window.location.href = onePayUrl
            return
        }
        // #endregion

        // Trigger form submission error if any of the steps are missed
        yield put(fatalErrorActions.setFatalError(true, errorMessage))
    } catch (e) {
        console.error(e)
        yield put(fatalErrorActions.setFatalError(true, errorMessage))
    }
}

function* initializeSubmitFormSaga() {
    yield takeEvery(BuyFormActionTypes.SUBMIT_BUY_FORM_UCS, submitFormSaga)
}

export default initializeSubmitFormSaga
